import { useState, useRef, useEffect } from "react";
import {
  useGetPlansQuery,
  useCheckoutSessionMutation,
} from "@/features/auth/api/authAPI";
import { useMe, useCurrentOrganization } from "@/Hooks";
import { useNavigate, useParams } from "react-router-dom";
import CustomSlider from "@/widgets/customSlider/ui/CustomSlider/CustomSlider";
import mixpanel from "@/shared/lib/mixpanel";
import styles from "./PricingInterface.module.scss";
import { User, Users, Check } from "lucide-react";

const soloFeatures = [
  {
    title: "Access all apps videos and screens. Gain clarity",
  },
  {
    title: "Traverse the library with search and find your edge",
  },
  {
    title: "20 new apps weekly. Our team at work for you",
  },
];

const teamFeatures = [
  {
    title: "Team workspaces",
  },
  {
    title: "Shared collections",
  },
  {
    title: "Admin & billing console",
  },
  {
    title: "Priority support",
  },
];

export const PricingInterface = () => {
  const [activeTab, setActiveTab] = useState<"solo" | "team">("solo");
  const [sliderValue, setSliderValue] = useState<number>(5);
  const [isProcessing, setIsProcessing] = useState(false);

  // Add referral state like in BillingPage
  const [toltReferral, setToltReferral] = useState();
  const [toltPartner, setToltPartner] = useState();

  const { me } = useMe();
  const currentOrg = useCurrentOrganization();
  const navigate = useNavigate();
  const params = useParams();
  const { data: apiDataPlans, isLoading } = useGetPlansQuery();
  const [createCheckoutSession] = useCheckoutSessionMutation();

  const previousActiveTabRef = useRef(activeTab);
  const previousSliderValueRef = useRef(sliderValue);

  // Add referral effect like in BillingPage
  useEffect(() => {
    if (window.tolt !== undefined && window.tolt !== null) {
      setToltReferral(window.tolt_referral);
      setToltPartner(window.tolt_data?.partner_id);
    }
  }, []);

  useEffect(() => {
    previousActiveTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    previousSliderValueRef.current = sliderValue;
  }, [sliderValue]);

  const handleTabClick = (tab: "solo" | "team") => {
    if (activeTab !== tab) {
      mixpanel.track("Billing Plan Type Selected", {
        plan_type_selected: tab === "solo" ? "individual" : "teams",
        previous_plan_type_selected:
          previousActiveTabRef.current === "solo" ? "individual" : "teams",
        organization_id: currentOrg?.uuid || null,
        user_id: me?.id || null,
        user_email: me?.email || null,
      });
      setActiveTab(tab);
    }
  };

  // Use exact same slider logic as BillingPlanTeams
  const onSliderChangeWithTracking = (newSliderValue: number | number[]) => {
    const currentVal = Array.isArray(newSliderValue)
      ? newSliderValue[0]
      : newSliderValue;

    if (currentVal !== previousSliderValueRef.current && apiDataPlans?.[0]) {
      const plan = apiDataPlans[0];
      const pricePerUser =
        plan.unit_amount / 100 / plan.recurring_interval_count;
      const newTotalPrice = currentVal * pricePerUser;

      mixpanel.track("Team Size Selected", {
        selected_team_size: currentVal,
        previous_team_size: previousSliderValueRef.current,
        calculated_total_price: parseFloat(newTotalPrice.toFixed(2)),
        price_per_user: parseFloat(pricePerUser.toFixed(2)),
        plan_id: plan.id,
        plan_currency: plan.currency,
        plan_interval: plan.recurring_interval,
        plan_interval_count: plan.recurring_interval_count,
        user_id: me?.id || null,
        user_email: me?.email || null,
        organization_id: currentOrg?.uuid || null,
      });

      setSliderValue(currentVal);
    } else {
      setSliderValue(currentVal);
    }
  };

  // Use exact same handleSelectPlan logic as BillingPage
  const handleSelectPlan = async (priceId: string, qte: number = 1) => {
    if (!currentOrg?.uuid) {
      navigate("/register");
      return;
    }

    setIsProcessing(true);

    try {
      const data = await createCheckoutSession({
        priceId: priceId,
        uuid: params.uuid || currentOrg.uuid,
        qte: qte,
        referralId: toltReferral,
        partnerId: toltPartner,
      }).unwrap();

      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        console.error("No redirect URL received from checkout session");
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Solo plan click handler like BillingPlanMonth
  const onUnlockProClick = () => {
    if (!apiDataPlans?.[0]) return;

    const plan = apiDataPlans[0];
    const monthlyPrice = (
      plan.unit_amount /
      100 /
      plan.recurring_interval_count
    ).toFixed(2);

    mixpanel.track("Plan Selected", {
      plan_type: "individual_monthly",
      plan_name: "Individual Monthly Pro",
      plan_id: plan.id,
      plan_price: parseFloat(monthlyPrice),
      plan_currency: plan.currency,
      plan_interval: plan.recurring_interval,
      plan_interval_count: plan.recurring_interval_count,
      user_id: me?.id || null,
      user_email: me?.email || null,
      organization_id: currentOrg?.uuid || null,
    });

    handleSelectPlan(plan.id, 1);
  };

  // Team plan click handler
  const onGetTeamPlanClick = () => {
    if (!apiDataPlans?.[0]) return;

    const plan = apiDataPlans[0];
    const pricePerUser = plan.unit_amount / 100 / plan.recurring_interval_count;
    const totalPrice = sliderValue * pricePerUser;

    mixpanel.track("Plan Selected", {
      plan_type: "team_monthly",
      plan_name: "Team Monthly Pro",
      plan_id: plan.id,
      plan_price: parseFloat(totalPrice.toFixed(2)),
      plan_currency: plan.currency,
      plan_interval: plan.recurring_interval,
      plan_interval_count: plan.recurring_interval_count,
      team_size: sliderValue,
      user_id: me?.id || null,
      user_email: me?.email || null,
      organization_id: currentOrg?.uuid || null,
    });

    handleSelectPlan(plan.id, sliderValue);
  };

  if (isLoading || !apiDataPlans?.[0]) {
    return <div className={styles.loadingSpinner}>Loading...</div>;
  }

  const plan = apiDataPlans[0];
  const monthlyPrice = (
    plan.unit_amount /
    100 /
    plan.recurring_interval_count
  ).toFixed(2);
  const pricePerUser = plan.unit_amount / 100 / plan.recurring_interval_count;
  const totalTeamPrice = (sliderValue * pricePerUser).toFixed(2);

  return (
    <div className={styles.pricingContainer}>
      <div className={styles.pricingHeader}>
        <h2 className={styles.pricingTitle}>Your new powerful advantage</h2>
        <p className={styles.pricingSubtitle}>
          More inspiration, better research. 1800+ top players. Love it or
          cancel in 2 clicks.
        </p>

        <div className={styles.tabContainer}>
          <button
            className={`${styles.tab} ${activeTab === "solo" ? styles.tabActive : ""}`}
            onClick={() => handleTabClick("solo")}
          >
            <User />
            Solo
          </button>
          <button
            className={`${styles.tab} ${activeTab === "team" ? styles.tabActive : ""}`}
            onClick={() => handleTabClick("team")}
          >
            <Users />
            Team
          </button>
        </div>
      </div>

      <div className={styles.pricingContent}>
        {activeTab === "solo" ? (
          <div className={styles.soloContent}>
            <ul className={styles.featuresList}>
              {soloFeatures.map((feature, index) => (
                <li className={styles.feature} key={index}>
                  <Check color="#4ade80" />
                  <p>{feature.title}</p>
                </li>
              ))}
            </ul>

            <div className={styles.pricingBox}>
              <div className={styles.price}>
                <span className={styles.priceAmount}>${monthlyPrice}</span>
                <span className={styles.pricePeriod}>/month</span>
              </div>
              <button
                className={styles.unlockButton}
                onClick={onUnlockProClick}
                disabled={isProcessing}
              >
                Unlock Pro
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.teamContent}>
            <div className={styles.teamDescription}>
              <p className={styles.teamText}>
                Collaborate with your team. Get shared access <br /> and tools
                designed for teamwork.
              </p>
              <p className={styles.teamSubtext}>
                Everything in Individual, plus:
              </p>

              <ul className={styles.featuresList}>
                {teamFeatures.map((feature, index) => (
                  <li className={styles.feature} key={index}>
                    <Check color="#4ade80" />
                    <p>{feature.title}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.teamPricing}>
              <div className={styles.sliderContainer}>
                <span className={styles.sliderLabel}>
                  Team size: {sliderValue}
                </span>
                <div className={styles.sliderRange}>
                  <CustomSlider
                    min={2}
                    max={20}
                    step={1}
                    value={sliderValue}
                    onChange={onSliderChangeWithTracking}
                  />
                </div>
              </div>

              <div className={styles.teamPriceSection}>
                <div className={styles.price}>
                  <span className={styles.priceAmount}>${totalTeamPrice}</span>
                  <span className={styles.pricePeriod}>/month</span>
                </div>
                <button
                  className={styles.unlockButton}
                  onClick={onGetTeamPlanClick}
                  disabled={isProcessing}
                >
                  Get Team Plan
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
